import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MobileMenuBar, Sidebar } from "@/components/pipeline/PipelineShell";
import { labStatus } from "@/lib/fbLabApi";
import { Link } from "react-router-dom";

// Super admin only, read-only: counts, signed-contract integrity, accounting totals, feeds and migration state.
const tone = (s) => (s === "Passed" ? "bg-emerald-100 text-emerald-900" : s === "Failed" ? "bg-red-100 text-red-900" : "bg-amber-100 text-amber-900");
const Badge = ({ s, testId }) => <span data-testid={testId} className={`px-2 py-0.5 rounded-full text-xs font-bold ${tone(s)}`}>{s}</span>;
const Box = ({ title, testId, children }) => <section data-testid={testId} className="bg-white border rounded-xl p-4 overflow-x-auto"><h2 className="font-semibold mb-2">{title}</h2>{children}</section>;
const fmt = (o) => (Object.keys(o || {}).length ? Object.entries(o).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ") : "none");

function Counts({ d }) {
  const cos = [...new Set([...d.companies.map((c) => c.id), ...Object.values(d.counts).flatMap(Object.keys)])];
  return (
    <Box title="Record counts by company" testId="health-counts">
      <table className="text-sm w-full"><thead><tr className="text-left text-stone-500"><th className="pr-4">Records</th>{cos.map((c) => <th key={c} className="pr-4">{c}</th>)}</tr></thead>
        <tbody>{Object.entries(d.counts).map(([k, v]) => <tr key={k} className="border-t"><td className="pr-4 py-1">{k}</td>{cos.map((c) => <td key={c} data-testid={`count-${k}-${c}`} className={`pr-4 ${c === "(missing)" && v[c] ? "text-red-700 font-bold" : ""}`}>{v[c] || 0}</td>)}</tr>)}</tbody></table>
      <p className="text-xs text-stone-600 mt-2">Users by role: {fmt(d.users_by_role)}</p>
    </Box>
  );
}

function Contracts({ c }) {
  return (
    <Box title={`Signed contracts — ${c.passed} passed · ${c.failed} failed · ${c.unable} unable to verify`} testId="health-contracts">
      <table className="text-xs w-full"><thead><tr className="text-left text-stone-500"><th>Contract</th><th>Company</th><th>Signature</th><th>Signed PDF</th><th>PDF opens</th><th>Ownership</th><th>Result</th></tr></thead>
        <tbody>{c.items.map((r) => <tr key={r.contract_id} data-testid={`health-contract-${r.contract_id}`} className="border-t">
          <td className="py-1 pr-2 font-mono">{r.contract_id.slice(0, 8)} v{r.version}</td><td>{r.company_id || "—"}</td>
          {["signature", "pdf", "pdf_accessible", "ownership", "status"].map((k) => <td key={k} className="pr-2"><Badge s={r[k]} /></td>)}</tr>)}</tbody></table>
    </Box>
  );
}

export default function PlatformHealth() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [lab, setLab] = useState(false);
  useEffect(() => { labStatus().then(() => setLab(true)).catch(() => setLab(false)); }, []);
  const run = () => { setBusy(true); setErr(""); api.get("/platform/health").then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Health check failed")).finally(() => setBusy(false)); };
  useEffect(() => { run(); }, []);
  return (
    <div className="min-h-screen bg-stone-100 lg:pl-60">
      <Sidebar />
      <MobileMenuBar />
      <main data-testid="platform-health" className="p-4 sm:p-8 max-w-6xl space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-3xl font-extrabold">Platform Health Check</h1>
          {d && <Badge s={d.overall} testId="health-overall" />}
          <Button data-testid="health-run" size="sm" variant="outline" disabled={busy} onClick={run}>{busy ? "Checking…" : "Run again"}</Button>
          {lab && <Link data-testid="health-fb-lab-link" to="/platform/fb-lab" className="text-sm font-semibold text-[#1877F2] hover:underline">Facebook Lab</Link>}
        </div>
        <p className="text-xs text-stone-600">Read-only. Nothing is changed, repaired or deleted.</p>
        {err && <p data-testid="health-error" className="text-sm text-red-700">{err}</p>}
        {d && <>
          <Box title="Migration" testId="health-migration">
            <p className="text-sm">Status <Badge s={d.migration.status} testId="health-migration-status" /> · completed: {d.migration.completed_at || "not marked complete"}</p>
            <p className="text-sm mt-1" data-testid="health-missing">Missing company: {fmt(d.migration.missing_company_id)}</p>
            <p className="text-sm" data-testid="health-invalid">Invalid company references: {fmt(d.migration.invalid_company_refs)}</p>
            <p className="text-sm" data-testid="health-mismatch">Ownership mismatches: {fmt(d.migration.ownership_mismatches)}</p>
            <p className="text-xs text-stone-600 mt-1">Startup report: {fmt(d.migration.startup_report)}</p>
          </Box>
          <Counts d={d} />
          <Contracts c={d.contracts} />
          <Box title="Accounting (existing calculations)" testId="health-accounting">
            {Object.entries(d.accounting).map(([cid, a]) => <div key={cid} data-testid={`health-accounting-${cid}`} className="text-sm border-t first:border-0 py-2">
              <b>{cid}</b> · {a.horses} horses · Initial {fmt(a.totals.initial)} · Final {fmt(a.totals.final)} · Expenses {fmt(a.totals.expenses)}
              <div className="text-xs text-stone-600">Invoices: {fmt(a.invoice_statuses)} · Payments: {fmt(a.payment_statuses)}{a.errors.length ? ` · ${a.errors.length} could not be calculated` : ""}</div></div>)}
          </Box>
          <Box title="Website feeds" testId="health-feeds">
            {d.feeds.map((f) => <div key={f.company_id} data-testid={`health-feed-${f.company_id}`} className="text-sm border-t first:border-0 py-2 flex flex-wrap gap-3 items-center">
              <b>{f.company_id}</b><Badge s={f.status} /><span>{f.published} published · {f.completed} completed</span>
              <span>{f.isolated ? "only own horses" : "CONTAINS OTHER COMPANIES' HORSES"}</span><span>{f.feed_key_configured ? "key configured" : "no feed key"}</span>
              <span className="font-mono text-xs">fingerprint {f.fingerprint}</span></div>)}
          </Box>
        </>}
      </main>
    </div>
  );
}
